import LegalLayout from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      testId="terms-page"
      eyebrow="Legal"
      title="Terms & Conditions"
      updated="February 2026"
    >
      <section>
        <h2>1. About Juke Transfers</h2>
        <p>
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern the transfer, chauffeur, and related
          transportation services provided by <strong>JUKE TRANSFERS j.d.o.o.</strong>
          (OIB: 05308456115), a Croatian limited liability company based in Split, Croatia
          (&ldquo;Juke Transfers&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By booking a service with us, you agree to be bound by
          these Terms.
        </p>
        <p>
          <strong>Booking model.</strong> Juke Transfers operates both as a direct transport
          provider and as an online booking platform / agent. Depending on the booking option
          selected at checkout, the customer will either:
        </p>
        <ol>
          <li>
            Pay a <strong>20% non-refundable deposit</strong> (Reservation / Booking Fee)
            online to Juke Transfers j.d.o.o., with the remaining <strong>80% balance paid
            in cash directly to the operating driver</strong> in the vehicle; or
          </li>
          <li>
            Pay the <strong>100% full amount online</strong> where applicable.
          </li>
        </ol>
        <p>
          When a partner driver operates the transfer on our behalf, Juke Transfers acts as
          a booking agent between the customer and the operating driver. The 20% deposit
          covers the reservation, dispatch and platform services rendered by Juke Transfers
          and is non-refundable except where required by law.
        </p>
      </section>

      <section>
        <h2>2. Bookings & Confirmations</h2>
        <p>
          A booking becomes binding only once we send a written confirmation (email or WhatsApp)
          containing the reference number, pickup details, and quoted price. Quotes generated on
          the website are estimates until confirmed by our team. The client is responsible for
          the accuracy of pickup address, drop-off address, date, time, passenger count, luggage,
          and any flight number provided at the time of booking.
        </p>
      </section>

      <section>
        <h2>3. Pricing</h2>
        <ul>
          <li>All prices are quoted in Euro (€) and are inclusive of applicable Croatian taxes.</li>
          <li>Fixed routes are published transparently. Distance-based transfers are calculated
              per kilometre, with a start fee.</li>
          <li>A nighttime surcharge of +20% applies to pickups between 22:00 and 06:00 local time.</li>
          <li>Suitcase fees, waiting-time fees, and any confirmed add-ons (bottled drinks,
              snacks, SIM card) are included in the final total price shown to the client.</li>
        </ul>
      </section>

      <section>
        <h2>4. Flight Delays & Waiting Time</h2>
        <p>
          Airport pickups include automated flight tracking. When a valid flight number is
          provided at the time of booking, our driver&rsquo;s arrival is aligned with the
          <strong> actual landing time</strong>, not the originally scheduled time.
        </p>
        <ul>
          <li>
            <strong>Free waiting window:</strong> the first <strong>60 minutes</strong> of
            waiting after the actual flight landing time are complimentary. This covers passport
            control, baggage claim, and reasonable delays reaching the meeting point.
          </li>
          <li>
            <strong>Additional waiting time:</strong> any waiting time caused by client-side
            delays beyond the free window (e.g. late arrival to the meeting point,
            unannounced schedule changes, extended stops) is charged at a
            <strong> fixed hourly rate of &euro;25 / hour</strong>, billed in 30-minute increments.
          </li>
          <li>
            For non-airport pickups, a <strong>15-minute</strong> grace period applies from the
            confirmed pickup time; the same &euro;25 / hour rate applies thereafter.
          </li>
          <li>
            If a flight is diverted, cancelled, or rescheduled to a different day, we will make
            every reasonable effort to reschedule at no extra cost. Additional dispatch beyond
            our control may be re-quoted.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Client Responsibilities</h2>
        <ul>
          <li>Provide accurate contact details (phone reachable on arrival day) and pickup
              location, including hotel / villa name and, where possible, a pinned map location.</li>
          <li>Be at the agreed meeting point on time. For airport pickups, our driver will wait
              at the Arrivals hall holding a name-board.</li>
          <li>Behave in a manner that respects the driver, the vehicle, and other passengers.
              We reserve the right to refuse or terminate service in cases of misconduct, illegal
              behaviour, or intoxication that jeopardises safety, without refund.</li>
          <li>Any damage caused to the vehicle by a passenger (spills of food/drink not
              provided by us, vomit, cigarette burns, physical damage) will be charged at the
              standard detailing / repair rate.</li>
        </ul>
      </section>

      <section>
        <h2>6. Luggage</h2>
        <p>
          Vehicle luggage capacity is stated on our fleet page and at the time of booking. Extra
          suitcases beyond the confirmed count may be refused at the driver&rsquo;s discretion if they
          exceed the safe capacity of the vehicle. Please declare oversized items (bikes, skis,
          surfboards) at the time of booking.
        </p>
      </section>

      <section>
        <h2>7. Liability</h2>
        <p>
          Juke Transfers operates a fully licensed and fully insured passenger fleet in
          accordance with Croatian law. Our liability is limited to the price paid for the
          booked service. We are not liable for indirect losses caused by force majeure events
          (weather, road closures, industrial action, missed connections not caused by us).
        </p>
      </section>

      <section>
        <h2>8. Cancellations & Refunds</h2>
        <p>
          Cancellations, refunds, and no-shows are governed by our separate{" "}
          <a href="/refund-policy" className="text-[#C5A059] hover:text-[#D4AF37] underline underline-offset-2">Cancellation &amp; Refund Policy</a>.
        </p>
      </section>

      <section>
        <h2>9. Data Protection</h2>
        <p>
          Personal data collected during the booking process is handled in accordance with our{" "}
          <a href="/privacy" className="text-[#C5A059] hover:text-[#D4AF37] underline underline-offset-2">Privacy Policy</a>
          {" "}and the EU General Data Protection Regulation (GDPR).
        </p>
      </section>

      <section>
        <h2>10. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Croatia. Any disputes will be
          submitted to the exclusive jurisdiction of the competent court in Split, Croatia.
        </p>
      </section>

      <section>
        <h2>11. Contact</h2>
        <p>
          Questions about these Terms can be sent to{" "}
          <a href="mailto:bookings@juketransfers.hr" className="text-[#C5A059] hover:text-[#D4AF37] underline underline-offset-2">bookings@juketransfers.hr</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
